import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
  fetchBusinessKeywordsCategoryById,
  createBusinessKeywordsCategory,
  createBusinessKeywordsCategoriesBulk,
  updateBusinessKeywordsCategory,
  clearCurrentItem,
  clearError
} from './businessKeywordsCategoriesSlice';
import { fetchBusinesses } from '../business/businessSlice';
import { fetchKeywordCategories } from '../keyword-category/keywordCategorySlice';
import { fetchCategories } from '../category/categorySlice';
import { fetchCuisines } from '../cuisine/cuisineSlice';
import { fetchBarPreferences } from '../bar-preference/barPreferenceSlice';
import { fetchActivities } from '../activity/activitySlice';
import { fetchTrends } from '../trend/trendSlice';
import ReactDropdown from '../../components/ReactDropdown';
import Loader from '../../components/Loader';
import toast from 'react-hot-toast';

const BusinessKeywordsCategoriesCreateEdit = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const { currentItem, loading, error } = useSelector(state => state.businessKeywordsCategories);
  const { items: businesses } = useSelector(state => state.business);
  const { items: keywordCategories } = useSelector(state => state.keywordCategory);
  const { items: categories } = useSelector(state => state.category);
  const { items: cuisines } = useSelector(state => state.cuisine);
  const { items: barPreferences } = useSelector(state => state.barPreference);
  const { items: activities } = useSelector(state => state.activity);

  // Constants for category types
  const CATEGORY_TYPES = {
    RESTAURANTS: 'Restaurants',
    BARS: 'Bars', 
    ACTIVITIES: 'Activities'
  };

  // Loading states
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingSubCategories] = useState(false);
  const [loadingTrends, setLoadingTrends] = useState(false);
  const [loadingBusinesses, setLoadingBusinesses] = useState(false);

  // Form state
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategories, setSelectedSubCategories] = useState([]);
  const [selectedTrends, setSelectedTrends] = useState([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState([]);
  const [filteredTrends, setFilteredTrends] = useState([]);

  const { control, handleSubmit, formState: { errors }, setValue } = useForm({
    mode: 'onChange'
  });

  // Locally suppress noisy global error toasts during batch submissions
  const [suppressErrorToasts, setSuppressErrorToasts] = useState(false);

  useEffect(() => {
    if (isEdit && id) {
      dispatch(fetchBusinessKeywordsCategoryById(id));
    } else {
      dispatch(clearCurrentItem());
    }

    return () => {
      dispatch(clearCurrentItem());
    };
  }, [dispatch, isEdit, id]);

  useEffect(() => {
    if (error && !suppressErrorToasts) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, suppressErrorToasts, dispatch]);

  // Load initial data on page load
  useEffect(() => {
    const loadInitialData = async () => {
      setLoadingCategories(true);
      try {
        await Promise.all([
          dispatch(fetchCategories({ page: 1, limit: 1000 })).unwrap(),
          dispatch(fetchKeywordCategories({ page: 1, limit: 1000 })).unwrap(),
          dispatch(fetchBusinesses({ page: 1, limit: 1000 })).unwrap(),
          dispatch(fetchCuisines({ page: 1, limit: 1000 })).unwrap(),
          dispatch(fetchBarPreferences({ page: 1, limit: 1000 })).unwrap(),
          dispatch(fetchActivities({ page: 1, limit: 1000 })).unwrap()
        ]);
      } catch (error) {
        console.error('Error loading initial data:', error);
        toast.error('Failed to load initial data');
      } finally {
        setLoadingCategories(false);
      }
    };

    loadInitialData();
  }, [dispatch]);

  // Clear subcategories when category changes
  useEffect(() => {
    if (!selectedCategory) {
      setSelectedSubCategories([]);
    }
  }, [selectedCategory]);

  // Load trends when category is selected
  useEffect(() => {
    const loadTrends = async () => {
      if (!selectedCategory) {
        setSelectedTrends([]);
        setFilteredTrends([]);
        return;
      }

      setLoadingTrends(true);
      try {
        const response = await dispatch(fetchTrends({ 
          page: 1, 
          limit: 1000, 
          categoryId: selectedCategory.value
        })).unwrap();
        
        
        // Set filtered trends from the response
        setFilteredTrends(response.trends || []);
        
        // Clear selected trends that are no longer available
        setSelectedTrends(prev => 
          prev.filter(trend => 
            (response.trends || []).some(ft => ft._id === trend.value)
          )
        );
      } catch (error) {
        console.error('Error loading trends:', error);
        toast.error('Failed to load trends');
        setFilteredTrends([]);
      } finally {
        setLoadingTrends(false);
      }
    };

    loadTrends();
  }, [dispatch, selectedCategory]);

  // When no category is selected, show all businesses and react to source list updates
  useEffect(() => {
    if (!selectedCategory) {
      setFilteredBusinesses(businesses);
    }
  }, [businesses, selectedCategory]);

  // When a category is selected, fetch filtered businesses from API
  useEffect(() => {
    const run = async () => {
      if (!selectedCategory) return;

      setLoadingBusinesses(true);
      try {
        const filters = {
          category: selectedCategory.value
        };

        // Add subcategory filters - API expects JSON string format
        if (selectedSubCategories.length > 0) {
          const subCategories = {};
          switch (selectedCategory.label) {
            case CATEGORY_TYPES.RESTAURANTS:
              subCategories.cuisines = selectedSubCategories.map(sc => sc.value);
              break;
            case CATEGORY_TYPES.BARS:
              subCategories.barPreferences = selectedSubCategories.map(sc => sc.value);
              break;
            case CATEGORY_TYPES.ACTIVITIES:
              subCategories.activities = selectedSubCategories.map(sc => sc.value);
              break;
          }
          filters.subCategories = JSON.stringify(subCategories);
        }

        // Add trend filters - API expects comma-separated string
        if (selectedTrends.length > 0) {
          filters.trends = selectedTrends.map(t => t.value).join(',');
        }

        const response = await dispatch(fetchBusinesses({ page: 1, limit: 1000, filters })).unwrap();
        setFilteredBusinesses(response.businesses || []);
      } catch (error) {
        console.error('Error filtering businesses:', error);
        toast.error('Failed to filter businesses');
        setFilteredBusinesses([]);
      } finally {
        setLoadingBusinesses(false);
      }
    };

    run();
  }, [dispatch, selectedCategory, selectedSubCategories, selectedTrends]);

  const handleFormSubmit = async (data) => {
    setSuppressErrorToasts(true);
    try {
      // Build shared payload for bulk creation
      const bulkPayload = {
        businessIds: data.businessIds,
        category: selectedCategory?.value || undefined,
        subCategories: {
          cuisines: selectedCategory?.label === CATEGORY_TYPES.RESTAURANTS ? selectedSubCategories.map(sc => sc.value) : [],
          barPreferences: selectedCategory?.label === CATEGORY_TYPES.BARS ? selectedSubCategories.map(sc => sc.value) : [],
          activities: selectedCategory?.label === CATEGORY_TYPES.ACTIVITIES ? selectedSubCategories.map(sc => sc.value) : []
        },
        trends: selectedTrends.map(t => t.value),
        keywordCategoryIds: data.keywordCategoryIds,
        status: Number(data.status)
      };

      if (isEdit) {
        // Edit operates on a single record
        await dispatch(updateBusinessKeywordsCategory({ id, data: {
          category: selectedCategory?.value,
          subCategories: bulkPayload.subCategories,
          trends: bulkPayload.trends,
          businessId: data.businessIds?.[0],
          keywordCategoryIds: data.keywordCategoryIds,
          status: Number(data.status)
        } })).unwrap();
        toast.success('Saved changes. Relationship updated.');
        navigate('/business-keywords-categories');
      } else {
        // Single bulk API call
        const result = await dispatch(createBusinessKeywordsCategoriesBulk(bulkPayload)).unwrap();
        const createdCount = Array.isArray(result) ? result.length : 0;
        if (createdCount > 0) {
          toast.success(`${createdCount} link${createdCount > 1 ? 's' : ''} processed successfully`);
          navigate('/business-keywords-categories');
        } else {
          toast('No changes made. The selected businesses are already linked.', { icon: 'ℹ️' });
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to save relationship. Please try again.');
    }
    finally {
      setSuppressErrorToasts(false);
    }
  };

  // Stable handlers
  const handleCategoryChange = useCallback((value) => {
    setSelectedCategory(value);
    setSelectedSubCategories([]);
    setSelectedTrends([]);
  }, []);

  const handleSubCategoriesChange = useCallback((value) => {
    setSelectedSubCategories(value || []);
  }, []);

  const handleTrendsChange = useCallback((value) => {
    setSelectedTrends(value || []);
  }, []);

  // Memoized options for better performance
  const categoryOptions = useMemo(() => 
    categories.map(category => ({
      value: category._id,
      label: category.label
    })), [categories]);

  const subCategoryOptions = useMemo(() => {
    if (!selectedCategory) return [];
    
    switch (selectedCategory.label) {
      case CATEGORY_TYPES.RESTAURANTS:
        return cuisines.map(cuisine => ({
          value: cuisine._id,
          label: cuisine.label
        }));
      case CATEGORY_TYPES.BARS:
        return barPreferences.map(bp => ({
          value: bp._id,
          label: bp.label
        }));
      case CATEGORY_TYPES.ACTIVITIES:
        return activities.map(activity => ({
          value: activity._id,
          label: activity.label
        }));
      default:
        return [];
    }
  }, [selectedCategory, cuisines, barPreferences, activities]);

  const trendOptions = useMemo(() => 
    filteredTrends.map(trend => ({
      value: trend._id,
      label: trend.label
    })), [filteredTrends]);

  const businessOptions = useMemo(() => 
    filteredBusinesses.map(business => ({
      value: business._id,
      label: business.title
    })), [filteredBusinesses]);

  const keywordCategoryOptions = useMemo(() => 
    keywordCategories.map(category => ({
        value: category._id,
        label: category.name
    })), [keywordCategories]);

  const statusOptions = useMemo(() => [
        { value: '0', label: 'Inactive' },
        { value: '1', label: 'Active' },
        { value: '2', label: 'Suspended' },
  ], []);

  // Set default values when currentItem changes
  useEffect(() => {
    if (isEdit && currentItem) {
      // Set category
      if (currentItem.category) {
        const categoryOption = categories.find(cat => cat._id === (currentItem.category._id || currentItem.category));
        if (categoryOption) {
          setSelectedCategory({ value: categoryOption._id, label: categoryOption.label });
        }
      }

      // Set subcategories
      if (currentItem.subCategories) {
        const subCategoryIds = (currentItem.subCategories.cuisines || [])
          .concat(currentItem.subCategories.barPreferences || [])
          .concat(currentItem.subCategories.activities || [])
          .map(sc => sc._id || sc);
        
        const filteredSubCategoryOptions = subCategoryOptions.filter(option => 
          subCategoryIds.includes(option.value)
        );
        setSelectedSubCategories(filteredSubCategoryOptions);
      }

      // Set trends - will be handled after filtered trends are loaded
      if (currentItem.trends) {
        // Store trend IDs to be set later when filtered trends are available
        const trendIds = currentItem.trends.map(t => t._id || t);
        // We'll set these in a separate useEffect after filteredTrends are loaded
      }

      // Set form values
      setValue('businessIds', currentItem.businessId ? [ (currentItem.businessId._id || currentItem.businessId) ] : []);
      setValue('keywordCategoryIds', currentItem.keywordCategoryIds?.map(kc => kc._id || kc) || []);
      setValue('status', String(currentItem.status ?? 1));
    } else {
      // Reset for create mode
      setSelectedCategory(null);
      setSelectedSubCategories([]);
      setSelectedTrends([]);
      setValue('businessIds', []);
      setValue('keywordCategoryIds', []);
      setValue('status', '1');
    }
    
    // Cleanup function to prevent memory leaks
    return () => {
      // No specific cleanup needed, but this ensures the effect is properly cleaned up
    };
  }, [currentItem, isEdit, setValue]);

  // Set trends in edit mode after filtered trends are loaded
  useEffect(() => {
    if (isEdit && currentItem && currentItem.trends && filteredTrends.length > 0) {
      const selectedTrendOptions = filteredTrends
        .filter(trend => currentItem.trends.some(t => (t._id || t) === trend._id))
        .map(trend => ({ value: trend._id, label: trend.label }));
      setSelectedTrends(selectedTrendOptions);
    }
    
    // Cleanup function to prevent memory leaks
    return () => {
      // No specific cleanup needed, but this ensures the effect is properly cleaned up
    };
  }, [isEdit, currentItem, filteredTrends]);

  // In edit mode, populate subcategories AFTER category and options are ready
  useEffect(() => {
    if (!isEdit || !currentItem || !selectedCategory) return;

    if (currentItem.subCategories) {
      const subCategoryIds = (currentItem.subCategories.cuisines || [])
        .concat(currentItem.subCategories.barPreferences || [])
        .concat(currentItem.subCategories.activities || [])
        .map(sc => sc._id || sc);

      const filteredSubCategoryOptions = subCategoryOptions.filter(option => 
        subCategoryIds.includes(option.value)
      );
      setSelectedSubCategories(filteredSubCategoryOptions);
    }
  }, [isEdit, currentItem, selectedCategory, subCategoryOptions]);

  if (loading && !currentItem && isEdit) {
    return <Loader size="lg" text="Loading business keyword category relationship..." />;
  }

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">
        {isEdit ? 'Edit Business Keyword Category Relationship' : 'Link Businesses to Keyword Category'}
      </h2>
      
      {(loadingCategories || loadingSubCategories || loadingTrends || loadingBusinesses) && (
        <div className="mb-4 p-2 bg-blue-50 text-blue-700 rounded flex items-center">
          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {loadingCategories ? 'Loading categories...' : 
           loadingSubCategories ? 'Loading subcategories...' :
           loadingTrends ? 'Loading trends...' :
           'Filtering businesses...'}
        </div>
      )}
      
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
        {/* Category Field */}
        <div className="mb-4">
          <label htmlFor="category" className="block mb-2 font-medium">
            Category
          </label>
          <ReactDropdown
            options={categoryOptions}
            value={selectedCategory}
            onChange={handleCategoryChange}
            isMulti={false}
            required={false}
            placeholder="Select a category (optional)..."
            isLoading={loadingCategories}
          />
        </div>

        {/* Sub Categories Field */}
        <div className="mb-4">
          <label htmlFor="subCategories" className="block mb-2 font-medium">Sub Categories</label>
          {selectedCategory ? (
            <ReactDropdown
              options={subCategoryOptions}
              value={selectedSubCategories}
              onChange={handleSubCategoriesChange}
              isMulti={true}
              showCheckboxes={true}
              paginate={true}
              pageSize={10}
              placeholder="Select subcategories..."
              isLoading={loadingSubCategories}
            />
          ) : (
            <div className="text-gray-500 italic p-3 border border-gray-300 rounded bg-gray-50">
              Please select a category first
            </div>
          )}
        </div>

        {/* Trends Field */}
        <div className="mb-4">
          <label htmlFor="trends" className="block mb-2 font-medium">Trends</label>
          {selectedCategory ? (
            <ReactDropdown
              options={trendOptions}
              value={selectedTrends}
              onChange={handleTrendsChange}
              isMulti={true}
              showCheckboxes={true}
              paginate={true}
              pageSize={10}
              placeholder="Select trends..."
              isLoading={loadingTrends}
            />
          ) : (
            <div className="text-gray-500 italic p-3 border border-gray-300 rounded bg-gray-50">
              Please select a category first
            </div>
          )}
        </div>

        {/* Businesses Field */}
        <div className="mb-4">
          <label htmlFor="businessIds" className="block mb-2 font-medium">
            Businesses <span className="text-red-500 ml-1" aria-label="required">*</span>
          </label>
          <Controller
            name="businessIds"
            control={control}
            rules={{ required: "Businesses are required" }}
            render={({ field }) => (
              <ReactDropdown
                options={businessOptions}
                value={businessOptions.filter(option => field.value?.includes(option.value))}
                onChange={(value) => field.onChange(value?.map(v => v.value) || [])}
                isMulti={true}
                showCheckboxes={true}
                showSelectAll={true}
                selectAllLabel="Select all"
                unselectAllLabel="Unselect all"
                paginate={true}
                pageSize={10}
                placeholder={loadingBusinesses ? "Loading businesses..." : selectedCategory ? "Select businesses..." : "Select businesses (showing all businesses)..."}
                isLoading={loadingBusinesses}
              />
            )}
          />
          {errors.businessIds && (
            <span className="text-red-500 text-sm mt-1" role="alert">{errors.businessIds.message}</span>
          )}
        </div>

        {/* Keyword Categories Field */}
        <div className="mb-4">
          <label htmlFor="keywordCategoryIds" className="block mb-2 font-medium">
            Keyword Categories <span className="text-red-500 ml-1" aria-label="required">*</span>
          </label>
          <Controller
            name="keywordCategoryIds"
            control={control}
            rules={{ required: "Keyword categories are required" }}
            render={({ field }) => (
              <ReactDropdown
                options={keywordCategoryOptions}
                value={keywordCategoryOptions.filter(option => field.value?.includes(option.value))}
                onChange={(value) => field.onChange(value?.map(v => v.value) || [])}
                isMulti={true}
                showCheckboxes={true}
                paginate={true}
                pageSize={10}
                placeholder="Select keyword categories..."
              />
            )}
          />
          {errors.keywordCategoryIds && (
            <span className="text-red-500 text-sm mt-1" role="alert">{errors.keywordCategoryIds.message}</span>
          )}
        </div>

        {/* Status Field */}
        <div className="mb-4">
          <label htmlFor="status" className="block mb-2 font-medium">Status</label>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <ReactDropdown
                options={statusOptions}
                value={statusOptions.find(option => option.value === field.value) || null}
                onChange={(value) => field.onChange(value?.value || '1')}
                isMulti={false}
                placeholder="Select status..."
              />
            )}
          />
        </div>

        <button 
          type="submit" 
          disabled={loading}
          className={`w-full px-4 py-2 rounded text-white ${
            loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'
          }`}
        >
          {loading ? 'Loading...' : (isEdit ? 'Update Relationship' : 'Link Businesses')}
        </button>
      </form>
    </div>
  );
};

export default BusinessKeywordsCategoriesCreateEdit;
